import { IConcept } from './types';

export const NODE_W = 232;
export const COLUMN_W = 360; // wider than a card: the side gutters carry dependency edges
export const GAP_Y = 22; // between stacked siblings
export const PADDING = 24;
export const COLUMN_HEADER_H = 52;

// Card height follows the title: header row + up to 3 title lines.
const HEADER_H = 38;
const LINE_H = 22;
const CHARS_PER_LINE = 24;
export const nodeHeight = (title: string): number => {
  const plain = title.replace(/\*/g, '');
  const lines = Math.min(3, Math.max(1, Math.ceil(plain.length / CHARS_PER_LINE)));
  return HEADER_H + lines * LINE_H;
};

export type ILayoutNode = {
  concept: IConcept;
  ghost: boolean; // requirement outside the current KLine filter
  x: number;
  y: number;
  height: number;
  depth: number; // zero-based level-column index
};

export type ILayoutEdge = {
  from: string; // requirement (parent, left)
  to: string; // dependent (child, right)
  pending: boolean; // the child is still waiting for this parent
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  route: 'across' | 'same-column';
  laneX?: number;
  note?: string;
};

export type ILayoutColumn = {
  level: number;
  x: number;
  width: number;
};

export type ILayout = {
  nodes: ILayoutNode[];
  edges: ILayoutEdge[];
  columns: ILayoutColumn[];
  width: number;
  height: number;
};

/**
 * Level-grouped dependency layout: every pedagogical level owns a band made
 * of one or more dependency columns. Within that band, requirements remain
 * left-to-right as in the original tree. The columns are wider than their
 * cards, leaving gutters for dependency lines. Requirements outside the
 * visible set are included as ghost nodes so every dependency stays
 * explainable.
 */
const range = (from: number, to: number): number[] => {
  const list: number[] = [];
  if (from <= to) {
    for (let i = from; i <= to; i++) list.push(i);
  } else {
    for (let i = from; i >= to; i--) list.push(i);
  }
  return list;
};

export const layoutTree = (visible: IConcept[], byCode: Map<string, IConcept>): ILayout => {
  const shown = new Map<string, IConcept>();
  visible.forEach((c) => shown.set(c.code, c));

  const ghosts = new Map<string, IConcept>();
  visible.forEach((c) => {
    c.requires.forEach((req) => {
      if (!shown.has(req) && byCode.has(req) && !ghosts.has(req)) {
        ghosts.set(req, byCode.get(req) as IConcept);
      }
    });
  });

  const all = new Map<string, IConcept>();
  ghosts.forEach((c, code) => all.set(code, c));
  shown.forEach((c, code) => all.set(code, c));

  const levels = Array.from(new Set(Array.from(all.values()).map((concept) => concept.level))).sort((a, b) => a - b);

  // Longest-path depth within each level. This restores the original visual
  // grammar (requirement on the left, dependent on the right) without mixing
  // cards from different pedagogical levels in the same column.
  const levelDepth = new Map<string, number>();
  const computeLevelDepth = (code: string, stack: Set<string>): number => {
    const cached = levelDepth.get(code);
    if (cached !== undefined) return cached;
    if (stack.has(code)) return 0;
    stack.add(code);
    const current = all.get(code) as IConcept;
    let depth = 0;
    current.requires.forEach((requiredCode) => {
      const required = all.get(requiredCode);
      if (required && required.level === current.level) {
        depth = Math.max(depth, computeLevelDepth(requiredCode, stack) + 1);
      }
    });
    stack.delete(code);
    levelDepth.set(code, depth);
    return depth;
  };
  all.forEach((_, code) => computeLevelDepth(code, new Set()));

  const columnCountByLevel = new Map<number, number>();
  levels.forEach((level) => columnCountByLevel.set(level, 1));
  all.forEach((concept, code) => {
    columnCountByLevel.set(
      concept.level,
      Math.max(columnCountByLevel.get(concept.level) ?? 1, (levelDepth.get(code) as number) + 1),
    );
  });

  const firstColumnByLevel = new Map<number, number>();
  let totalColumns = 0;
  levels.forEach((level) => {
    firstColumnByLevel.set(level, totalColumns);
    totalColumns += columnCountByLevel.get(level) as number;
  });
  const columnOf = (code: string): number => {
    const concept = all.get(code) as IConcept;
    return (firstColumnByLevel.get(concept.level) as number) + (levelDepth.get(code) as number);
  };

  // Nodes are grouped by level + dependency depth, then ordered by KLine position.
  const columns = new Map<number, string[]>();
  all.forEach((_, code) => {
    const column = columnOf(code);
    if (!columns.has(column)) columns.set(column, []);
    (columns.get(column) as string[]).push(code);
  });
  columns.forEach((codes) => {
    codes.sort((a, b) => {
      const ca = all.get(a) as IConcept;
      const cb = all.get(b) as IConcept;
      if (ca.klineId !== cb.klineId) return (ca.klineId ?? 0) - (cb.klineId ?? 0);
      if (ca.klinePos !== cb.klinePos) return ca.klinePos - cb.klinePos;
      return ca.code.localeCompare(cb.code);
    });
  });

  const maxColumn = Math.max(0, totalColumns - 1);

  // Extra right margin so the last column's right port is never covered by
  // the container scrollbar.
  const SCROLLBAR_MARGIN = 48;
  const width = PADDING * 2 + totalColumns * COLUMN_W + SCROLLBAR_MARGIN;

  const heights = new Map<string, number>();
  all.forEach((concept, code) => heights.set(code, nodeHeight(concept.title)));

  const succs = new Map<string, string[]>();
  all.forEach((concept, code) => {
    concept.requires.forEach((req) => {
      if (!all.has(req)) return;
      if (!succs.has(req)) succs.set(req, []);
      (succs.get(req) as string[]).push(code);
    });
  });

  // Vertical placement: a node wants to sit at the average height of its
  // relations (barycenter), not stacked at the top. Relaxation sweeps —
  // align to prerequisites (left), then to dependents (right), then to
  // prerequisites again — each sweep re-sorts the column by desired height
  // and resolves overlaps downwards preserving that order.
  const top = new Map<string, number>();
  const center = (code: string): number => (top.get(code) as number) + (heights.get(code) as number) / 2;

  // Positions float freely (may go negative) during the sweeps — clamping the
  // column top here acts as a downward ratchet across iterations. The whole
  // canvas is normalized back to the top margin at the end.
  const placeColumn = (codes: string[], desired: Map<string, number>) => {
    const order = [...codes].sort((a, b) => (desired.get(a) as number) - (desired.get(b) as number));
    let prevBottom = Number.NEGATIVE_INFINITY;
    order.forEach((code) => {
      const h = heights.get(code) as number;
      const want = (desired.get(code) as number) - h / 2;
      const y = prevBottom === Number.NEGATIVE_INFINITY ? want : Math.max(want, prevBottom + GAP_Y);
      top.set(code, y);
      prevBottom = y + h;
    });
  };

  // Initial stacking in kline order.
  for (let d = 0; d <= maxColumn; d++) {
    const codes = columns.get(d) ?? [];
    let y = PADDING + COLUMN_HEADER_H;
    codes.forEach((code) => {
      top.set(code, y);
      y += (heights.get(code) as number) + GAP_Y;
    });
  }

  const sweep = (align: 'preds' | 'succs') => {
    const order = maxColumn < 1 ? [] : align === 'preds' ? range(1, maxColumn) : range(maxColumn - 1, 0);
    order.forEach((d) => {
      const codes = columns.get(d) ?? [];
      const desired = new Map<string, number>();
      codes.forEach((code) => {
        const concept = all.get(code) as IConcept;
        const partners = (
          align === 'preds' ? concept.requires.filter((r) => all.has(r)) : succs.get(code) ?? []
        ).filter((partner) => columnOf(partner) !== d);
        if (partners.length > 0) {
          desired.set(code, partners.reduce((sum, partner) => sum + center(partner), 0) / partners.length);
        } else {
          desired.set(code, center(code));
        }
      });
      placeColumn(codes, desired);
    });
  };

  sweep('preds');
  sweep('succs');
  sweep('preds');

  // Compaction: unrelated groups sharing a column can end up far apart (each
  // aligned to partners elsewhere), leaving dead vertical voids. Pack the
  // connected components tightly: each keeps its internal alignment, and the
  // whole component shifts up as far as its columns allow.
  const componentOf = new Map<string, number>();
  let componentCount = 0;
  const assignComponent = (start: string) => {
    const queue = [start];
    componentOf.set(start, componentCount);
    while (queue.length) {
      const code = queue.pop() as string;
      const concept = all.get(code) as IConcept;
      const neighbours = [...concept.requires.filter((r) => all.has(r)), ...(succs.get(code) ?? [])];
      neighbours.forEach((other) => {
        if (!componentOf.has(other)) {
          componentOf.set(other, componentCount);
          queue.push(other);
        }
      });
    }
    componentCount++;
  };
  all.forEach((_, code) => {
    if (!componentOf.has(code)) assignComponent(code);
  });

  const componentNodes = new Map<number, string[]>();
  componentOf.forEach((component, code) => {
    if (!componentNodes.has(component)) componentNodes.set(component, []);
    (componentNodes.get(component) as string[]).push(code);
  });

  const componentOrder = Array.from(componentNodes.entries()).sort(
    (a, b) =>
      Math.min(...a[1].map((code) => top.get(code) as number)) -
      Math.min(...b[1].map((code) => top.get(code) as number)),
  );

  const columnBottom = new Map<number, number>();
  componentOrder.forEach(([, codes]) => {
    let allowedDelta = Number.POSITIVE_INFINITY;
    codes.forEach((code) => {
      const col = columnOf(code);
      const floor = (columnBottom.get(col) ?? PADDING + COLUMN_HEADER_H - GAP_Y) + GAP_Y;
      allowedDelta = Math.min(allowedDelta, (top.get(code) as number) - floor);
    });
    codes.forEach((code) => {
      const y = (top.get(code) as number) - allowedDelta;
      top.set(code, y);
      const col = columnOf(code);
      columnBottom.set(
        col,
        Math.max(columnBottom.get(col) ?? Number.NEGATIVE_INFINITY, y + (heights.get(code) as number)),
      );
    });
  });

  const nodes: ILayoutNode[] = [];
  const pos = new Map<string, { x: number; y: number; h: number }>();
  let height = 0;
  for (let d = 0; d <= maxColumn; d++) {
    const codes = columns.get(d) ?? [];
    const x = PADDING + d * COLUMN_W + (COLUMN_W - NODE_W) / 2;
    codes.forEach((code) => {
      const concept = all.get(code) as IConcept;
      const h = heights.get(code) as number;
      const y = top.get(code) as number;
      pos.set(code, { x, y, h });
      nodes.push({
        concept,
        ghost: ghosts.has(code) && !shown.has(code),
        x,
        y,
        height: h,
        depth: d,
      });
      height = Math.max(height, y + h + PADDING);
    });
  }

  const edges: ILayoutEdge[] = [];
  const sameColumnEdges = new Map<number, number>();
  all.forEach((concept, code) => {
    concept.requires.forEach((req) => {
      if (!all.has(req)) return;
      const from = pos.get(req) as { x: number; y: number; h: number };
      const to = pos.get(code) as { x: number; y: number; h: number };
      const fromColumn = columnOf(req);
      const toColumn = columnOf(code);
      const sameColumn = fromColumn === toColumn;
      const lane = sameColumnEdges.get(fromColumn) ?? 0;
      if (sameColumn) sameColumnEdges.set(fromColumn, lane + 1);
      edges.push({
        from: req,
        to: code,
        pending: concept.pendingRequires.includes(req),
        x1: sameColumn || fromColumn < toColumn ? from.x + NODE_W : from.x,
        y1: from.y + from.h / 2,
        x2: sameColumn || fromColumn > toColumn ? to.x + NODE_W : to.x,
        y2: to.y + to.h / 2,
        route: sameColumn ? 'same-column' : 'across',
        laneX: sameColumn ? from.x + NODE_W + 14 + (lane % 4) * 13 : undefined,
        note: concept.requireNotes?.[req],
      });
    });
  });

  const layoutColumns = levels.map((level) => ({
    level,
    x: PADDING + (firstColumnByLevel.get(level) as number) * COLUMN_W,
    width: (columnCountByLevel.get(level) as number) * COLUMN_W,
  }));

  return { nodes, edges, columns: layoutColumns, width, height };
};
